import React, { useState } from 'react';
import { Paperclip, Upload, Download, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmActionDialog } from './ConfirmActionDialog';

export const fmtFileSize = (n) => (n == null ? '' : n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

// Optional document expiry health: expired / expiring ≤30d (mirrors licence expiry logic).
export const attachmentExpiryInfo = (expiry) => {
  if (!expiry) return null;
  const d = new Date(expiry);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(23, 59, 59, 999);
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days < 0) return { level: 'expired', label: 'Expired', cls: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800' };
  if (days <= 30) return { level: 'expiring', label: `Expires in ${days}d`, cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800' };
  return null;
};

// Worst expiry state across a person's documents: 'expired' > 'expiring' > null.
export const worstAttachmentExpiry = (attachments) => {
  let worst = null;
  for (const a of attachments || []) {
    const info = attachmentExpiryInfo(a.expiry_date);
    if (info?.level === 'expired') return 'expired';
    if (info?.level === 'expiring') worst = 'expiring';
  }
  return worst;
};

export const DOC_TYPES = [
  { value: 'licence', label: 'Licence' },
  { value: 'id', label: 'ID' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'other', label: 'Other' },
];
export const VEHICLE_DOC_TYPES = [
  { value: 'registration', label: 'Registration' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'inspection', label: 'Inspection/RWC' },
  { value: 'other', label: 'Other' },
];
const docTypeLabel = (v, types = DOC_TYPES) => types.find((t) => t.value === v)?.label || 'Other';

const DocTypeFilterSelect = ({ value, onChange, testid, className = '', docTypes = DOC_TYPES }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`h-8 rounded-md border border-input bg-background px-2 text-[11px] ${className}`}
    title="Filter documents by type"
    data-testid={testid}
  >
    <option value="all">All types</option>
    {docTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
  </select>
);

// Opens the tab synchronously (inside the click) then streams the authenticated blob into it → no popup blocker.
export const openAttachmentInNewTab = async (api, att) => {
  const win = window.open('', '_blank');
  try {
    const r = await api.get(att.url.replace(/^\/api/, ''), { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(r.data);
    if (win) win.location = blobUrl; else window.open(blobUrl, '_blank');
  } catch (e) {
    if (win) win.close();
    toast.error(e?.response?.status === 404 ? 'Attachment file not found' : 'Could not open attachment');
  }
};

export const downloadAttachmentFile = async (api, att) => {
  try {
    const r = await api.get(att.url.replace(/^\/api/, ''), { responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = att.name || 'attachment'; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch { toast.error('Could not download attachment'); }
};

const AttachmentRow = ({ att, idx, api, onRemove, onMeta, testPrefix, docTypes = DOC_TYPES }) => {
  const exp = attachmentExpiryInfo(att.expiry_date);
  return (
    <li className="px-3 py-2 space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <button type="button" className="flex-1 min-w-[120px] text-left truncate text-blue-600 dark:text-blue-400 hover:underline" onClick={() => openAttachmentInNewTab(api, att)} title="Open in new tab" data-testid={`${testPrefix}-open-${idx}`}>{att.name || 'Attachment'}</button>
        {!onMeta && att.note && (
          <span className="text-sm font-medium text-sky-700 dark:text-sky-300 truncate max-w-[340px] flex-shrink" title={att.note} data-testid={`${testPrefix}-note-${idx}`}>{att.note}</span>
        )}
        {!onMeta && (
          <Badge variant="outline" className="text-[10px] flex-shrink-0" data-testid={`${testPrefix}-type-badge-${idx}`}>{docTypeLabel(att.doc_type, docTypes)}</Badge>
        )}
        {exp && <Badge variant="outline" className={`text-[10px] flex-shrink-0 ${exp.cls}`} data-testid={`${testPrefix}-expiry-badge-${idx}`}>{exp.label}</Badge>}
        <span className="text-[11px] text-muted-foreground flex-shrink-0">{[att.content_type?.split('/')[1]?.toUpperCase(), fmtFileSize(att.size)].filter(Boolean).join(' • ')}</span>
        <Button type="button" size="icon" variant="outline" className="h-8 w-8 flex-shrink-0" onClick={() => downloadAttachmentFile(api, att)} title="Download" data-testid={`${testPrefix}-download-${idx}`}><Download className="w-4 h-4" /></Button>
        {onRemove && (
          <Button type="button" size="icon" variant="ghost" className="h-8 w-8 text-red-500 flex-shrink-0" onClick={() => onRemove(att)} title="Remove (deletes the file)" data-testid={`${testPrefix}-remove-${idx}`}><X className="w-4 h-4" /></Button>
        )}
      </div>
      {onMeta ? (
        <div className="flex items-center gap-2 flex-wrap pl-5">
          <select
            value={att.doc_type || 'other'}
            onChange={(e) => onMeta(att, { doc_type: e.target.value })}
            className="h-8 rounded-md border border-input bg-background px-2 text-[11px] flex-shrink-0"
            title="Document type"
            data-testid={`${testPrefix}-type-select-${idx}`}
          >
            {docTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input
            type="date"
            value={att.expiry_date || ''}
            onChange={(e) => onMeta(att, { expiry_date: e.target.value || null })}
            className="h-8 rounded-md border border-input bg-background px-2 text-[11px] flex-shrink-0"
            title="Expiry date (optional)"
            data-testid={`${testPrefix}-expiry-input-${idx}`}
          />
          <input
            type="text"
            defaultValue={att.note || ''}
            maxLength={200}
            placeholder="Note — what does this document cover?"
            onBlur={(e) => { if (e.target.value.trim() !== (att.note || '')) onMeta(att, { note: e.target.value }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            className="h-8 flex-1 min-w-[160px] rounded-md border border-input bg-background px-2 text-[11px]"
            data-testid={`${testPrefix}-note-input-${idx}`}
          />
        </div>
      ) : null}
    </li>
  );
};

/** Generic immediate-upload documents manager (shared by People and Vehicle edit dialogs). */
const AttachmentsManager = ({ entityId, baseUrl, attachments, onChange, prefix, docTypes, label, hideLabel, saveFirstMsg }) => {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const shownAttachments = typeFilter === 'all' ? attachments : attachments.filter((a) => (a.doc_type || 'other') === typeFilter);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length || !entityId) return;
    setUploading(true);
    let latest = attachments;
    let ok = 0;
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post(baseUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        latest = r.data.attachments;
        ok += 1;
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || 'Upload failed'}`);
      }
    }
    if (ok) { toast.success(ok === 1 ? 'Document uploaded' : `${ok} documents uploaded`); onChange?.(latest); }
    setUploading(false);
  };

  const remove = async (att) => {
    try {
      const r = await api.delete(`${baseUrl}/${att.id}`);
      onChange?.(r.data.attachments);
      toast.success('Document removed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to remove document'); }
  };

  const updateMeta = async (att, patch) => {
    try {
      const r = await api.patch(`${baseUrl}/${att.id}`, patch);
      onChange?.(r.data.attachments);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update document'); }
  };

  return (
    <div className="space-y-1.5 sm:col-span-2" data-testid={`${prefix}-section`}>
      {!hideLabel && <Label className="text-xs">{label}</Label>}
      {!entityId ? (
        <p className="text-[11px] text-muted-foreground" data-testid={`${prefix}-save-first`}>{saveFirstMsg}</p>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer?.files); }}
          className={`space-y-1.5 rounded-md border-2 border-dashed p-2 -m-0.5 transition-colors ${dragOver ? 'border-blue-500 bg-blue-50/60 dark:bg-blue-950/30' : 'border-transparent'}`}
          data-testid={`${prefix}-dropzone`}
        >
          <div className="flex items-center gap-2">
            <input type="file" id={`${prefix}-file`} className="hidden" multiple accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.csv,.txt" onChange={(e) => { upload(e.target.files); e.target.value = ''; }} data-testid={`${prefix}-file-input`} />
            <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById(`${prefix}-file`).click()} disabled={uploading} data-testid={`${prefix}-upload-btn`}>
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Add files
            </Button>
            <span className="text-[11px] text-muted-foreground">
              {dragOver ? 'Drop files to upload' : `${attachments.length ? `${attachments.length} attached` : 'No documents yet'} — or drag & drop files here`}
            </span>
            {attachments.length > 1 && (
              <DocTypeFilterSelect value={typeFilter} onChange={setTypeFilter} testid={`${prefix}-type-filter`} className="ml-auto" docTypes={docTypes} />
            )}
          </div>
          {attachments.length > 0 && (
            shownAttachments.length === 0 ? (
              <p className="text-[11px] text-muted-foreground" data-testid={`${prefix}-filter-empty`}>No {docTypeLabel(typeFilter, docTypes).toLowerCase()} documents.</p>
            ) : (
              <ul className="divide-y rounded-md border text-xs" data-testid={`${prefix}-list`}>
                {shownAttachments.map((a, i) => <AttachmentRow key={a.id} att={a} idx={i} api={api} onRemove={setPendingRemove} onMeta={updateMeta} testPrefix={prefix} docTypes={docTypes} />)}
              </ul>
            )
          )}
        </div>
      )}
      <ConfirmActionDialog
        open={!!pendingRemove}
        onOpenChange={(o) => { if (!o) setPendingRemove(null); }}
        title="Remove document?"
        message={<>Remove <b>{pendingRemove?.name}</b>? The file will be permanently deleted from storage.</>}
        confirmLabel="Remove"
        onConfirm={() => remove(pendingRemove)}
        testPrefix={`${prefix}-remove-confirm`}
      />
    </div>
  );
};

/** Documents section inside the Driver / Staff / Passenger edit dialogs. Uploads attach immediately. */
export const PersonAttachments = ({ personType, person, onChange, testPrefix, hideLabel = false }) => (
  <AttachmentsManager
    entityId={person?.id}
    baseUrl={`/people/${personType}/${person?.id}/attachments`}
    attachments={person?.attachments || []}
    onChange={onChange}
    prefix={testPrefix || `${personType}-attachment`}
    docTypes={DOC_TYPES}
    label="Documents (licence / ID / certificates)"
    hideLabel={hideLabel}
    saveFirstMsg={`Save the ${personType} first, then add documents from Edit.`}
  />
);

/** Documents section inside the Vehicle edit dialog — registration / insurance / inspection.
 *  Per-account isolated, with the same expiry chips + document-expiry warning notifications. */
export const VehicleAttachments = ({ deviceId, attachments, onChange, testPrefix, hideLabel = false }) => (
  <AttachmentsManager
    entityId={deviceId}
    baseUrl={`/fleet/vehicles/${deviceId}/attachments`}
    attachments={attachments || []}
    onChange={onChange}
    prefix={testPrefix || 'vehicle-attachment'}
    docTypes={VEHICLE_DOC_TYPES}
    label="Documents (registration / insurance / inspection)"
    hideLabel={hideLabel}
    saveFirstMsg="Save the vehicle first, then add documents from Edit."
  />
);

/** Read-only viewer opened from the paperclip button on People rows. */
// Read-only documents list (dashboard cards): view/download only — uploads live in the Edit dialogs.
export const ReadOnlyAttachmentsList = ({ attachments, docTypes = DOC_TYPES, testPrefix = 'docs-card' }) => {
  const { api } = useAuth();
  const list = attachments || [];
  return (
    <ul className="divide-y rounded-md border text-sm" data-testid={`${testPrefix}-list`}>
      {list.map((a, i) => <AttachmentRow key={a.id || i} att={a} idx={i} api={api} testPrefix={testPrefix} docTypes={docTypes} />)}
      {!list.length && <li className="px-3 py-4 text-center text-muted-foreground text-xs">No documents</li>}
    </ul>
  );
};

export const AttachmentsViewerDialog = ({ open, onOpenChange, title, subtitle, attachments, testPrefix = 'attachments-viewer' }) => {
  const { api } = useAuth();
  const [typeFilter, setTypeFilter] = useState('all');
  const all = attachments || [];
  const shown = typeFilter === 'all' ? all : all.filter((a) => (a.doc_type || 'other') === typeFilter);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setTypeFilter('all'); onOpenChange(o); }}>
      <DialogContent className="w-[95vw] max-w-4xl" data-testid={`${testPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Paperclip className="w-4 h-4" /> {title || 'Attachments'}</DialogTitle>
          {subtitle && <DialogDescription>{subtitle}</DialogDescription>}
        </DialogHeader>
        {all.length > 1 && (
          <DocTypeFilterSelect value={typeFilter} onChange={setTypeFilter} testid={`${testPrefix}-type-filter`} className="self-start" />
        )}
        <ul className="divide-y rounded-md border text-sm" data-testid={`${testPrefix}-list`}>
          {shown.map((a, i) => <AttachmentRow key={a.id || i} att={a} idx={i} api={api} testPrefix={testPrefix} />)}
          {!shown.length && <li className="px-3 py-4 text-center text-muted-foreground text-xs">No documents</li>}
        </ul>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid={`${testPrefix}-close`}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
